import { MAP_LAB_BAND_LOWER_PERCENTILE, MAP_LAB_BAND_UPPER_PERCENTILE, MAP_LAB_ROBUST_MAX_PERCENTILE } from "./constants.js";

// PREVIEW helper: robust max for relative shading opacity.

export function robustShadeMax(values) {
  const positive = values.filter((value) => value > 0).sort((a, b) => a - b);
  if (positive.length === 0) return 0;
  const index = Math.ceil(MAP_LAB_ROBUST_MAX_PERCENTILE * positive.length) - 1;
  return positive[Math.min(positive.length - 1, Math.max(0, index))];
}

// PREVIEW (Map Lab shading): map a metric value to a 0..1 opacity norm relative to the
// robust max, with a sqrt curve so mid/low areas stay visible instead of clumping at the
// floor. Clamped to 1 so the busiest few areas (at/above the robust max) read fully shaded.

export function shadeNorm(value, robustMax) {
  if (!(value > 0) || !(robustMax > 0)) return 0;
  return Math.min(1, Math.sqrt(value / robustMax));
}

// PREVIEW (Map Lab banded coloring): the 25th/75th-percentile sick-rate cut points for a
// feature set, so "3 bands" mode splits areas ~25/50/25 by their standing in the real
// spread (not fixed rate thresholds). Only areas that actually have reports feed the
// percentiles — no-data areas are grayed, so they'd skew a naive quantile toward 0.
// Returns null when there aren't enough data areas to form meaningful cut points, in
// which case every area falls back to the middle band.

export function sickRateQuantiles(rows) {
  const rates = rows
    .filter((row) => row.totalCount > 0 && row.healthScore != null)
    .map((row) => row.healthScore)
    .sort((a, b) => a - b);
  if (rates.length < 3) return null;
  const at = (percentile) => {
    const index = Math.min(rates.length - 1, Math.max(0, Math.ceil(percentile * rates.length) - 1));
    return rates[index];
  };
  return { lower: at(MAP_LAB_BAND_LOWER_PERCENTILE), upper: at(MAP_LAB_BAND_UPPER_PERCENTILE) };
}

// PREVIEW (Map Lab banded coloring): place one area in a 0/1/2 band (yellow/orange/red)
// against the set's percentile cut points. No-data areas (healthScore null) get the
// middle band as an inert placeholder — they're grayed by suppression regardless.

export function sickBand(healthScore, quantiles) {
  if (!quantiles || healthScore == null) return 1;
  if (healthScore < quantiles.lower) return 0;
  if (healthScore >= quantiles.upper) return 2;
  return 1;
}

export function sickRateCssColor(rate) {
  const stops = [
    [0.43, [254, 204, 92]],
    [0.58, [253, 141, 60]],
    [0.73, [189, 0, 38]],
  ];
  const value = Number(rate);
  if (!Number.isFinite(value)) return "#94a3b8";
  const toRgb = ([r, g, b]) => `rgb(${r}, ${g}, ${b})`;
  if (value <= stops[0][0]) return toRgb(stops[0][1]);
  if (value >= stops[stops.length - 1][0]) return toRgb(stops[stops.length - 1][1]);
  for (let i = 0; i < stops.length - 1; i += 1) {
    const [x0, c0] = stops[i];
    const [x1, c1] = stops[i + 1];
    if (value >= x0 && value <= x1) {
      const t = (value - x0) / (x1 - x0);
      return toRgb(c0.map((channel, j) => Math.round(channel + (c1[j] - channel) * t)));
    }
  }
  return "#94a3b8";
}
